
import React, { useEffect, useState, useMemo } from 'react';
import { Card, Button } from '../components/UI';
import { apiRequest } from '../utils/api';
import { useLeague } from '../components/LeagueContext';
import { RefreshCw, Timer, Zap, AlertOctagon, Award, TrendingUp, Users, Car, BarChart2, Shield, Trophy, Skull, Rocket, ChevronDown, ChevronUp, Info, History, Medal, Flag, Crown, Target, Flame } from 'lucide-react';
import { PageHero } from '../components/PageHero';
import { hasActiveSeason, NoActiveSeason } from '../components/NoActiveSeason';

interface ChartSeries {
    id: string;
    name: string;
    color: string;
    data: { round: number; points: number }[];
}

interface RankingItem {
    username: string;
    team_color: string;
    value: number;
}

interface AllRankings {
    penalties: RankingItem[];
    quali: RankingItem[];
    fastest_lap: RankingItem[];
    dotd: RankingItem[];
    clean_races: RankingItem[];
    podiums: RankingItem[];
    dnfs: RankingItem[];
    hard_charger: RankingItem[];
    consistency: RankingItem[];
}

// ─── ANIMATED NUMBER ─────────────────────────────────────────
const AnimNumber: React.FC<{ value: number; delay?: number }> = ({ value, delay = 0 }) => {
    const [display, setDisplay] = useState(0);
    useEffect(() => {
        const timeout = setTimeout(() => {
            const duration = 800;
            const start = performance.now();
            const animate = (now: number) => {
                const elapsed = now - start;
                const progress = Math.min(elapsed / duration, 1);
                const eased = 1 - Math.pow(1 - progress, 3);
                setDisplay(Math.round(eased * value));
                if (progress < 1) requestAnimationFrame(animate);
            };
            requestAnimationFrame(animate);
        }, delay);
        return () => clearTimeout(timeout);
    }, [value, delay]);
    return <>{display}</>;
};

// ─── POSITION BADGE ──────────────────────────────────────────
const PosBadge: React.FC<{ pos: number }> = ({ pos }) => {
    const styles: Record<number, string> = {
        1: 'bg-gradient-to-br from-yellow-400 to-amber-600 text-black shadow-lg shadow-yellow-500/20',
        2: 'bg-gradient-to-br from-gray-300 to-gray-500 text-black',
        3: 'bg-gradient-to-br from-orange-400 to-orange-700 text-black',
    };
    return (
        <div className={`w-6 h-6 rounded-md flex items-center justify-center text-[10px] font-black shrink-0 ${styles[pos] || 'bg-gray-800 text-gray-500'}`}>
            {pos}
        </div>
    );
};

// ─── STAT CARD ───────────────────────────────────────────────
const StatCard: React.FC<{
    title: string;
    icon: any;
    data: RankingItem[];
    unit: string;
    accentColor: string;
    description?: string;
    animDelay?: number;
}> = ({ title, icon: Icon, data, unit, accentColor, description, animDelay = 0 }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const displayData = isExpanded ? data : data.slice(0, 5);
    const maxVal = data.length > 0 ? data[0].value : 1;

    return (
        <div
            className="group relative overflow-hidden rounded-[1.75rem] border border-white/[0.075] bg-gradient-to-br from-[#171a24] to-[#0e1017] shadow-[0_24px_70px_rgba(0,0,0,0.22)] transition-all duration-500 hover:-translate-y-1 hover:border-white/[0.14]"
            style={{ animationDelay: `${animDelay}ms`, animation: 'statCardIn 0.5s ease-out both' }}
        >
            {/* Top accent gradient */}
            <div className="h-[2px] w-full" style={{ background: `linear-gradient(90deg, ${accentColor}, transparent)` }} />

            <div className="p-6">
                {/* Header */}
                <div className="flex justify-between items-start mb-5">
                    <div className="flex items-center gap-3">
                        <div
                            className="w-11 h-11 rounded-2xl flex items-center justify-center transition-transform group-hover:scale-110"
                            style={{ backgroundColor: accentColor + '18', border: `1px solid ${accentColor}30` }}
                        >
                            <Icon size={16} style={{ color: accentColor }} />
                        </div>
                        <div>
                            <h3 className="text-[11px] font-black uppercase tracking-[0.15em] text-white/90">{title}</h3>
                            {description && <p className="text-[9px] text-gray-600 mt-0.5 leading-tight max-w-[180px]">{description}</p>}
                        </div>
                    </div>
                </div>

                {data && data.length > 0 ? (
                    <div className="space-y-0">
                        {displayData.map((item, idx) => {
                            const barWidth = maxVal > 0 ? (item.value / maxVal) * 100 : 0;
                            return (
                                <div
                                    key={idx}
                                    className={`flex items-center gap-2.5 py-[7px] group/row transition-colors ${idx === 0 ? 'mb-1' : ''} ${idx > 0 ? 'border-t border-gray-800/30' : ''}`}
                                >
                                    <PosBadge pos={idx + 1} />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between mb-1">
                                            <span className={`text-[11px] font-bold truncate ${idx === 0 ? 'text-white' : 'text-gray-400'}`}>
                                                {item.username}
                                            </span>
                                            <div className="flex items-center gap-1.5 shrink-0 ml-2">
                                                <span className={`font-mono text-[11px] font-black tabular-nums ${idx === 0 ? 'text-white' : 'text-gray-500'}`}>
                                                    {item.value}
                                                </span>
                                                <span className="text-[7px] font-bold text-gray-700 uppercase">{unit}</span>
                                            </div>
                                        </div>
                                        {/* Progress bar */}
                                        <div className="h-[3px] bg-gray-800/60 rounded-full overflow-hidden">
                                            <div
                                                className="h-full rounded-full transition-all duration-1000 ease-out"
                                                style={{
                                                    width: `${barWidth}%`,
                                                    background: idx === 0
                                                        ? `linear-gradient(90deg, ${accentColor}, ${accentColor}88)`
                                                        : `linear-gradient(90deg, ${item.team_color || '#555'}88, ${item.team_color || '#555'}33)`,
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}

                        {data.length > 5 && (
                            <button
                                onClick={() => setIsExpanded(!isExpanded)}
                                className="mt-3 w-full py-2 flex items-center justify-center gap-1.5 text-[9px] font-black uppercase tracking-[0.2em] text-gray-600 hover:text-gray-400 transition-colors"
                            >
                                {isExpanded ? <><ChevronUp size={11} /> Weniger</> : <><ChevronDown size={11} /> Alle {data.length} anzeigen</>}
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center py-8 text-gray-800">
                        <BarChart2 size={28} className="mb-2 opacity-30" />
                        <div className="text-[9px] font-bold uppercase tracking-widest opacity-40">Keine Daten</div>
                    </div>
                )}
            </div>
        </div>
    );
};

// ─── POINTS CHART ────────────────────────────────────────────
const PointsChart: React.FC<{ data: ChartSeries[]; type: 'DRIVER' | 'TEAM' }> = ({ data, type }) => {
    const [visibleIds, setVisibleIds] = useState<string[]>([]);
    const [hoveredRound, setHoveredRound] = useState<number | null>(null);
    const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number } | null>(null);

    useEffect(() => {
        if (data && data.length > 0) {
            const sorted = [...data].sort((a, b) => {
                const lastA = a.data[a.data.length - 1]?.points || 0;
                const lastB = b.data[b.data.length - 1]?.points || 0;
                return lastB - lastA;
            });
            setVisibleIds(sorted.slice(0, 6).map(s => s.id));
        }
    }, [data, type]);

    const toggleLine = (id: string) => {
        setVisibleIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const sortedForLegend = useMemo(() => {
        if (!data) return [];
        return [...data].sort((a, b) => {
            const lastA = a.data && a.data.length > 0 ? a.data[a.data.length - 1].points : 0;
            const lastB = b.data && b.data.length > 0 ? b.data[b.data.length - 1].points : 0;
            return lastB - lastA;
        });
    }, [data]);

    if (!data || data.length === 0) return (
        <div className="p-16 text-center border border-dashed border-gray-800 rounded-2xl bg-[#0a0a12] flex flex-col items-center">
            <TrendingUp size={48} className="text-gray-800 mb-4" />
            <div className="text-gray-600 font-black text-base uppercase italic">Keine Chart-Daten</div>
            <p className="text-[10px] text-gray-700 mt-2 max-w-sm">
                Sobald das erste Rennergebnis eingetragen ist, erscheint hier der Saisonverlauf.
            </p>
        </div>
    );

    const width = 1000;
    const height = 420;
    const padL = 55, padR = 30, padT = 30, padB = 45;

    let maxPoints = 0;
    let maxRound = 0;
    data.forEach(s => {
        if (s.data) {
            s.data.forEach(p => {
                if (p.points > maxPoints) maxPoints = p.points;
                if (p.round > maxRound) maxRound = p.round;
            });
        }
    });
    if (maxRound < 1) maxRound = 1;
    if (maxPoints < 10) maxPoints = 10;
    
    // Nice rounding for Y axis: round up to next "nice" number
    const niceStep = maxPoints <= 50 ? 10 : maxPoints <= 100 ? 20 : maxPoints <= 250 ? 50 : 100;
    maxPoints = Math.ceil(maxPoints * 1.1 / niceStep) * niceStep;

    // Generate clean grid values
    const gridCount = 5;
    const gridStep = maxPoints / gridCount;
    const gridValues = Array.from({ length: gridCount + 1 }, (_, i) => Math.round(i * gridStep));

    const xScale = (r: number) => padL + (r / maxRound) * (width - padL - padR);
    const yScale = (p: number) => height - padB - (p / maxPoints) * (height - padT - padB);

    const activeSeries = data.filter(d => visibleIds.includes(d.id));

    const hoveredData = hoveredRound !== null
        ? activeSeries.map(s => {
            const pt = s.data.find(p => p.round === hoveredRound);
            return pt ? { name: s.name, color: s.color, points: pt.points } : null;
        }).filter(Boolean).sort((a: any, b: any) => b.points - a.points)
        : [];

    return (
        <div className="overflow-hidden rounded-[1.75rem] border border-white/[0.075] bg-gradient-to-br from-[#171a24] to-[#0e1017] shadow-[0_24px_70px_rgba(0,0,0,0.22)]">
            {/* Chart Header */}
            <div className="px-6 py-4 border-b border-gray-800/40 flex justify-between items-center">
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-f1-red/10 border border-f1-red/20 flex items-center justify-center">
                        <TrendingUp size={15} className="text-f1-red" />
                    </div>
                    <div>
                        <h3 className="text-sm font-black uppercase italic tracking-tight text-white">
                            {type === 'DRIVER' ? 'Fahrer' : 'Konstrukteurs'}-WM Verlauf
                        </h3>
                        <p className="text-[9px] text-gray-600 font-bold uppercase tracking-[0.2em]">Punkteentwicklung Runde für Runde</p>
                    </div>
                </div>
                <div className="flex items-center gap-2 text-[9px] text-gray-600">
                    <Info size={12} />
                    <span className="hidden sm:inline font-bold uppercase tracking-wider">Legende klickbar</span>
                </div>
            </div>

            {/* SVG Chart */}
            <div className="relative p-4">
                <svg
                    viewBox={`0 0 ${width} ${height}`}
                    className="w-full h-auto"
                    style={{ maxHeight: '420px' }}
                    onMouseLeave={() => { setHoveredRound(null); setTooltipPos(null); }}
                >
                    <defs>
                        {activeSeries.map(s => (
                            <linearGradient key={s.id} id={`area-${s.id}`} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={s.color} stopOpacity="0.15" />
                                <stop offset="100%" stopColor={s.color} stopOpacity="0" />
                            </linearGradient>
                        ))}
                    </defs>

                    {/* Horizontal grid */}
                    {gridValues.map((val, idx) => {
                        const y = yScale(val);
                        return (
                            <g key={idx}>
                                <line x1={padL} y1={y} x2={width - padR} y2={y} stroke="#1a1a24" strokeWidth="1" />
                                <text x={padL - 12} y={y + 4} fill="#3a3a4a" fontSize="10" fontWeight="700" textAnchor="end" fontFamily="monospace">{val}</text>
                            </g>
                        );
                    })}

                    {/* Round labels */}
                    {Array.from({ length: maxRound + 1 }).map((_, i) => {
                        if (i === 0) return null;
                        const x = xScale(i);
                        return (
                            <g key={i}>
                                <text x={x} y={height - padB + 22} fill="#3a3a4a" fontSize="9" fontWeight="700" textAnchor="middle" fontFamily="monospace">R{i}</text>
                                <line x1={x} y1={padT} x2={x} y2={height - padB} stroke={hoveredRound === i ? '#333' : '#111'} strokeWidth="1" />
                            </g>
                        );
                    })}

                    {/* Area fills */}
                    {activeSeries.map(s => {
                        if (s.data.length < 2) return null;
                        let areaPath = `M ${xScale(s.data[0].round)} ${yScale(s.data[0].points)}`;
                        s.data.slice(1).forEach(p => { areaPath += ` L ${xScale(p.round)} ${yScale(p.points)}`; });
                        areaPath += ` L ${xScale(s.data[s.data.length - 1].round)} ${height - padB} L ${xScale(s.data[0].round)} ${height - padB} Z`;
                        return <path key={`area-${s.id}`} d={areaPath} fill={`url(#area-${s.id})`} />;
                    })}

                    {/* Lines */}
                    {activeSeries.map(s => {
                        let dPath = '';
                        s.data.forEach((p, i) => {
                            const x = xScale(p.round);
                            const y = yScale(p.points);
                            dPath += (i === 0 ? `M ${x} ${y}` : ` L ${x} ${y}`);
                        });
                        return (
                            <g key={s.id}>
                                <path d={dPath} fill="none" stroke={s.color} strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" opacity="0.15" />
                                <path d={dPath} fill="none" stroke={s.color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                {s.data.map((p, i) => (
                                    <circle
                                        key={i}
                                        cx={xScale(p.round)}
                                        cy={yScale(p.points)}
                                        r={hoveredRound === p.round ? 5 : 3.5}
                                        fill="#0a0a12"
                                        stroke={s.color}
                                        strokeWidth={hoveredRound === p.round ? 3 : 2}
                                        className="transition-all duration-150"
                                    />
                                ))}
                                {s.data.length > 0 && (
                                    <text
                                        x={xScale(s.data[s.data.length - 1].round) + 8}
                                        y={yScale(s.data[s.data.length - 1].points) + 4}
                                        fill={s.color}
                                        fontSize="9"
                                        fontWeight="800"
                                        fontFamily="monospace"
                                    >
                                        {s.data[s.data.length - 1].points}
                                    </text>
                                )}
                            </g>
                        );
                    })}

                    {/* Hover columns */}
                    {Array.from({ length: maxRound }).map((_, i) => {
                        const round = i + 1;
                        const x = xScale(round);
                        return (
                            <rect
                                key={round}
                                x={x - (width - padL - padR) / maxRound / 2}
                                y={padT}
                                width={(width - padL - padR) / maxRound}
                                height={height - padT - padB}
                                fill="transparent"
                                onMouseEnter={(e) => {
                                    setHoveredRound(round);
                                    const svg = e.currentTarget.ownerSVGElement;
                                    if (svg) {
                                        const pt = svg.createSVGPoint();
                                        pt.x = e.clientX; pt.y = e.clientY;
                                        const cursorPt = pt.matrixTransform(svg.getScreenCTM()?.inverse());
                                        setTooltipPos({ x: cursorPt.x, y: padT + 10 });
                                    }
                                }}
                            />
                        );
                    })}

                    {/* Hover line */}
                    {hoveredRound !== null && (
                        <line x1={xScale(hoveredRound)} y1={padT} x2={xScale(hoveredRound)} y2={height - padB} stroke="var(--f1-red)" strokeWidth="1" strokeDasharray="3 3" opacity="0.6" />
                    )}

                    {/* Tooltip */}
                    {hoveredRound !== null && tooltipPos && hoveredData.length > 0 && (
                        <foreignObject x={Math.min(tooltipPos.x + 10, width - 160)} y={tooltipPos.y} width="150" height={hoveredData.length * 20 + 30} style={{ overflow: 'visible' }}>
                            <div style={{ background: '#1a1a24', border: '1px solid #333', borderRadius: '8px', padding: '8px 10px', fontSize: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.6)' }}>
                                <div style={{ fontWeight: 900, color: '#888', fontSize: '8px', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '4px' }}>Runde {hoveredRound}</div>
                                {(hoveredData as any[]).map((d: any, i: number) => (
                                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '2px 0', gap: '8px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <div style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: d.color, flexShrink: 0 }} />
                                            <span style={{ color: '#ccc', fontWeight: 700 }}>{d.name}</span>
                                        </div>
                                        <span style={{ color: '#fff', fontWeight: 900, fontFamily: 'monospace' }}>{d.points}</span>
                                    </div>
                                ))}
                            </div>
                        </foreignObject>
                    )}
                </svg>
            </div>

            {/* Legend */}
            <div className="px-4 pb-4">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-1.5">
                    {sortedForLegend.map(s => {
                        const isActive = visibleIds.includes(s.id);
                        const pts = s.data && s.data.length > 0 ? s.data[s.data.length - 1].points : 0;
                        return (
                            <button
                                key={s.id}
                                onClick={() => toggleLine(s.id)}
                                className={`flex items-center justify-between px-2.5 py-1.5 rounded-lg text-[10px] transition-all duration-200 border ${isActive ? 'bg-gray-800/60 border-gray-700/80 text-white' : 'bg-transparent border-gray-800/30 text-gray-700 hover:text-gray-500'}`}
                            >
                                <div className="flex items-center gap-1.5 truncate">
                                    <div className={`w-2 h-2 rounded-sm shrink-0 transition-opacity ${isActive ? '' : 'opacity-30'}`} style={{ backgroundColor: s.color }} />
                                    <span className="font-bold uppercase truncate">{s.name}</span>
                                </div>
                                <span className="font-mono font-black ml-1.5 tabular-nums">{pts}</span>
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

// ─── ALL-TIME STATS ──────────────────────────────────────────
interface AllTimeStats {
    wins: RankingItem[];
    podiums: RankingItem[];
    poles: RankingItem[];
    fastest_laps: RankingItem[];
    races: RankingItem[];
    dnfs: RankingItem[];
    clean_races: RankingItem[];
    dotd: RankingItem[];
}

// ─── MAIN COMPONENT ─────────────────────────────────────────
export const Statistics: React.FC = () => {
    const { seasons, isLoading: leagueLoading } = useLeague();
    const [rankings, setRankings] = useState<AllRankings>({
        penalties: [], quali: [], fastest_lap: [], dotd: [],
        clean_races: [], podiums: [], dnfs: [], hard_charger: [], consistency: []
    });
    const [allTime, setAllTime] = useState<AllTimeStats>({
        wins: [], podiums: [], poles: [], fastest_laps: [],
        races: [], dnfs: [], clean_races: [], dotd: []
    });
    const [chartDataDriver, setChartDataDriver] = useState<ChartSeries[]>([]);
    const [chartDataTeam, setChartDataTeam] = useState<ChartSeries[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingAllTime, setLoadingAllTime] = useState(false);
    const [mainTab, setMainTab] = useState<'RANKINGS' | 'CHARTS' | 'ALLTIME'>('RANKINGS');
    const [chartTab, setChartTab] = useState<'DRIVER' | 'TEAM'>('DRIVER');
    const [selectedSeason, setSelectedSeason] = useState<string>('');

    const loadAll = async () => {
        setLoading(true);
        try {
            const rankUrl = selectedSeason ? `stats_rankings.php?seasonId=${selectedSeason}` : 'stats_rankings.php';
            const rankRes = await apiRequest<AllRankings>(rankUrl);
            if (rankRes) setRankings(rankRes);

            const cdDriver = await apiRequest<ChartSeries[]>('chart_data.php?type=driver');
            if (Array.isArray(cdDriver)) setChartDataDriver(cdDriver);

            const cdTeam = await apiRequest<ChartSeries[]>('chart_data.php?type=team');
            if (Array.isArray(cdTeam)) setChartDataTeam(cdTeam);
        } catch (e) {
            console.error("Stats Load Error:", e);
        } finally {
            setLoading(false);
        }
    };

    const fetchAllTime = async () => {
        setLoadingAllTime(true);
        try {
            const res = await apiRequest<AllTimeStats>('all_time_stats.php');
            setAllTime(res);
        } catch (e) {
            console.error(e);
        } finally {
            setLoadingAllTime(false);
        }
    };

    useEffect(() => { loadAll(); }, [selectedSeason]);
    useEffect(() => {
        if (mainTab === 'ALLTIME' && allTime.wins.length === 0) {
            fetchAllTime();
        }
    }, [mainTab]);

    const rankingCards = [
        { key: 'quali', title: 'Qualifying-Spezialist', icon: Target, unit: 'POLES', color: '#a855f7', desc: 'Meiste Pole Positions der Saison' },
        { key: 'fastest_lap', title: 'Rundenjäger', icon: Zap, unit: 'FLs', color: '#eab308', desc: 'Meiste schnellste Rennrunden' },
        { key: 'podiums', title: 'Podium-Jäger', icon: Trophy, unit: 'PODS', color: '#f97316', desc: 'Top-3-Platzierungen' },
        { key: 'clean_races', title: 'Fair-Racer', icon: Shield, unit: 'CLEAN', color: '#3b82f6', desc: 'Rennen ohne Strafen' },
        { key: 'hard_charger', title: 'Aufholjäger', icon: Rocket, unit: 'GAINS', color: '#10b981', desc: 'Meiste gutgemachte Plätze' },
        { key: 'penalties', title: 'Strafen König', icon: AlertOctagon, unit: 'SEK', color: '#ef4444', desc: 'Gesammelte Zeitstrafen' },
        { key: 'dnfs', title: 'DNF Magnet', icon: Skull, unit: 'DNFs', color: '#6b7280', desc: 'Meiste Ausfälle der Saison' },
        { key: 'consistency', title: 'Konstanz-König', icon: BarChart2, unit: 'AVG', color: '#78716c', desc: 'Beste Ø Platzierung' },
        { key: 'dotd', title: 'Driver of the Day', icon: Award, unit: 'VOTES', color: '#e10600', desc: 'Community-Votings gewonnen' },
    ];

    const allTimeCards = [
        { key: 'wins', title: 'All-Time Siege', icon: Crown, unit: 'WINS', color: '#eab308', desc: 'Ewige Bestenliste der Rennsiege' },
        { key: 'podiums', title: 'All-Time Podiums', icon: Medal, unit: 'PODS', color: '#f97316', desc: 'Top 3 über alle Saisons' },
        { key: 'poles', title: 'All-Time Poles', icon: Zap, unit: 'POLES', color: '#a855f7', desc: 'Ewige Pole-Position Liste' },
        { key: 'fastest_laps', title: 'All-Time Fastest Laps', icon: Timer, unit: 'FLs', color: '#06b6d4', desc: 'Schnellste Runden aller Zeiten' },
        { key: 'races', title: 'All-Time Starts', icon: Flag, unit: 'STARTS', color: '#3b82f6', desc: 'Meiste Rennteilnahmen' },
        { key: 'clean_races', title: 'All-Time Clean Races', icon: Shield, unit: 'CLEAN', color: '#10b981', desc: 'Straffreie Rennen gesamt' },
        { key: 'dnfs', title: 'All-Time DNFs', icon: Skull, unit: 'DNFs', color: '#6b7280', desc: 'Ausfälle über alle Saisons' },
        { key: 'dotd', title: 'All-Time DOTD', icon: Flame, unit: 'DOTD', color: '#ec4899', desc: 'Meiste Driver of the Day Titel' },
    ];

    const tabItems = [
        { id: 'RANKINGS' as const, label: 'Rankings', icon: Trophy },
        { id: 'ALLTIME' as const, label: 'Historie', icon: History },
        { id: 'CHARTS' as const, label: 'Verläufe', icon: TrendingUp },
    ];

    return (
        <div className="sdc-feature-page sdc-statistics-page max-w-7xl mx-auto space-y-8 animate-fade-in pb-24">
            <PageHero
                eyebrow="SDC Data Lab"
                title="Statistiken"
                description="Rekorde, Trends und Performance auf einen Blick – von der aktuellen Saison bis zur ewigen Bestenliste."
                icon={BarChart2}
                accent="#22d3ee"
                stats={[
                    { label: 'Fahrer', value: chartDataDriver.length },
                    { label: 'Teams', value: chartDataTeam.length },
                    { label: 'Kategorien', value: rankingCards.length },
                ]}
                actions={<Button variant="secondary" icon={RefreshCw} onClick={loadAll}>Aktualisieren</Button>}
            />

            {!leagueLoading && !hasActiveSeason(seasons) && (
                <NoActiveSeason compact description="Saisonbezogene Rankings pausieren aktuell. Die ewigen Bestenlisten bleiben hier verfügbar; vollständige Saisonabschlüsse findest du zusätzlich in der Historie." />
            )}

            {loading ? (
                <div className="py-32 text-center flex flex-col items-center gap-4">
                    <div className="relative w-14 h-14">
                        <div className="absolute inset-0 border-2 border-gray-800 rounded-full" />
                        <div className="absolute inset-0 border-2 border-t-f1-red rounded-full animate-spin" />
                    </div>
                    <span className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-600 animate-pulse">Daten werden geladen...</span>
                </div>
            ) : (
                <>
                    {/* ── TABS ────────────────────────────────── */}
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                        <div className="sdc-feature-tabs">
                            {tabItems.map(t => (
                                <button
                                    key={t.id}
                                    onClick={() => {
                                        setMainTab(t.id);
                                        if (t.id === 'ALLTIME' && allTime.wins.length === 0) fetchAllTime();
                                    }}
                                    className={`px-5 md:px-7 py-3 rounded-[1rem] text-[10px] font-black uppercase tracking-[0.15em] flex items-center gap-2 transition-all duration-200 ${
                                        mainTab === t.id
                                            ? 'bg-f1-red text-white shadow-lg shadow-f1-red/20'
                                            : 'text-gray-600 hover:text-gray-400'
                                    }`}
                                >
                                    <t.icon size={14} /> {t.label}
                                </button>
                            ))}
                        </div>

                        {mainTab === 'RANKINGS' && (
                            <div className="flex items-center gap-2 rounded-[1.1rem] border border-white/[0.075] bg-[#0e1017]/90 px-4 py-3">
                                <span className="text-[9px] font-black text-gray-600 uppercase tracking-wider">Saison</span>
                                <select
                                    className="bg-transparent text-[11px] font-bold text-white outline-none cursor-pointer uppercase tracking-wide"
                                    value={selectedSeason}
                                    onChange={(e) => setSelectedSeason(e.target.value)}
                                >
                                    <option value="">Aktuelle Saison</option>
                                    {seasons.map(s => (
                                        <option key={s.id} value={s.id}>{s.name} ({s.year})</option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>

                    {/* ── RANKINGS TAB ────────────────────────── */}
                    {mainTab === 'RANKINGS' && (
                        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                            {rankingCards.map((card, idx) => (
                                <StatCard
                                    key={card.key}
                                    title={card.title}
                                    icon={card.icon}
                                    data={(rankings as any)[card.key] || []}
                                    unit={card.unit}
                                    accentColor={card.color}
                                    description={card.desc}
                                    animDelay={idx * 60}
                                />
                            ))}
                        </div>
                    )}

                    {/* ── ALL-TIME TAB ────────────────────────── */}
                    {mainTab === 'ALLTIME' && (
                        <div>
                            {loadingAllTime ? (
                                <div className="py-24 text-center flex flex-col items-center gap-4">
                                    <div className="relative w-12 h-12">
                                        <div className="absolute inset-0 border-2 border-gray-800 rounded-full" />
                                        <div className="absolute inset-0 border-2 border-t-yellow-500 rounded-full animate-spin" />
                                    </div>
                                    <span className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-600 animate-pulse">Historie wird geladen...</span>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                                    {allTimeCards.map((card, idx) => (
                                        <StatCard
                                            key={card.key}
                                            title={card.title}
                                            icon={card.icon}
                                            data={(allTime as any)[card.key] || []}
                                            unit={card.unit}
                                            accentColor={card.color}
                                            description={card.desc}
                                            animDelay={idx * 60}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── CHARTS TAB ──────────────────────────── */}
                    {mainTab === 'CHARTS' && (
                        <div className="space-y-4">
                            <div className="flex w-fit items-center gap-1 rounded-[1.2rem] border border-white/[0.075] bg-[#0e1017]/90 p-1.5">
                                <button
                                    onClick={() => setChartTab('DRIVER')}
                                    className={`px-5 py-2 rounded-md text-[10px] font-black uppercase tracking-[0.12em] flex items-center gap-2 transition-all ${
                                        chartTab === 'DRIVER' ? 'bg-white/10 text-white' : 'text-gray-600 hover:text-gray-400'
                                    }`}
                                >
                                    <Users size={13} /> Fahrer-WM
                                </button>
                                <button
                                    onClick={() => setChartTab('TEAM')}
                                    className={`px-5 py-2 rounded-md text-[10px] font-black uppercase tracking-[0.12em] flex items-center gap-2 transition-all ${
                                        chartTab === 'TEAM' ? 'bg-white/10 text-white' : 'text-gray-600 hover:text-gray-400'
                                    }`}
                                >
                                    <Car size={13} /> Konstrukteurs-WM
                                </button>
                            </div>

                            <PointsChart data={chartTab === 'DRIVER' ? chartDataDriver : chartDataTeam} type={chartTab} />
                        </div>
                    )}

                    {/* ── FOOTER ──────────────────────────────── */}
                    <div className="flex items-center justify-between rounded-[1.5rem] border border-white/[0.06] bg-[#0c0c14] px-6 py-5">
                        <div className="flex items-center gap-3">
                            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                            <span className="text-[9px] font-bold text-gray-600 uppercase tracking-[0.2em]">
                                Alle Daten basieren auf offiziellen Rennergebnissen
                            </span>
                        </div>
                        <span className="text-[9px] font-mono text-gray-700">{new Date().toLocaleTimeString()}</span>
                    </div>
                </>
            )}

            {/* Inline keyframes */}
            <style>{`
                @keyframes statCardIn {
                    from { opacity: 0; transform: translateY(12px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </div>
    );
};
